import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Bot, Loader2, Plus, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, money, fmtDate } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { PlanPicker } from "@/components/PlanPicker";
import { DocumentsPanel } from "@/components/DocumentsPanel";
import { PolicyDocButtons } from "@/components/PolicyDocButtons";
import { ClaimDialog } from "@/components/ClaimDialog";
import { ClaimsList } from "@/components/ClaimsList";
import { IdentityPanel } from "@/components/IdentityPanel";
import { NewMessageDialog, SupportList } from "@/components/SupportPanel";
import { stateName, coverageLabel, levelLabel, limitLabel, METHOD_LABEL } from "@/lib/constants";

const Section = ({ title, children, action }) => (
  <section className="mt-10">
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-lg font-bold tracking-tight">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Empty = ({ text, testId }) => (
  <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid={testId}>{text}</div>
);

const QuoteCard = ({ q }) => {
  const [showDocs, setShowDocs] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid={`quote-card-${q.id}`}>
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-semibold">{coverageLabel(q.coverage_type)} · {stateName(q.state)}</p>
            <StatusPill status={q.status} testId={`quote-status-${q.id}`} />
          </div>
          <p className="mt-1 text-sm text-slate-500">{q.coverage_type === "general_liability" ? "General liability" : levelLabel(q.coverage_level)} · {limitLabel(q.liability_limit)} · Submitted {fmtDate(q.created_at)} · Estimate ${q.estimate_low}–${q.estimate_high}/mo</p>
          {q.bundle_discount && <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid={`quote-bundle-${q.id}`}>Business bundle: 10% off applied</p>}
          {q.admin_note && <p className="mt-2 text-sm text-slate-700" data-testid={`quote-note-${q.id}`}>{q.renewal_of ? q.admin_note : `Note from our team: ${q.admin_note}`}</p>}
        </div>
        <div className="flex items-center gap-3">
          {q.status === "priced" && <p className="font-mono text-xl font-semibold" data-testid={`quote-final-price-${q.id}`}>{money(q.monthly_premium)}<span className="text-sm text-slate-500">/mo</span></p>}
          <Button size="sm" variant="outline" onClick={() => setShowDocs(!showDocs)} data-testid={`quote-docs-toggle-${q.id}`}>
            <Paperclip className="mr-1 h-4 w-4" /> Documents
          </Button>
        </div>
      </div>
      {showDocs && <div className="mt-4 border-t border-slate-100 pt-4"><DocumentsPanel quoteId={q.id} compact /></div>}
      {q.status === "priced" && <PlanPicker quote={q} />}
    </div>
  );
};

const PolicyRow = ({ p }) => (
  <div className="rounded-lg border border-slate-200 bg-white transition-colors hover:border-blue-400" data-testid={`policy-card-${p.id}`}>
  <Link to={`/policies/${p.id}`} className="group flex items-center justify-between p-5" data-testid={`policy-open-${p.id}`}>
    <div>
      <p className="font-mono text-sm text-slate-500">{p.policy_number}</p>
      <p className="mt-1 font-semibold">{coverageLabel(p.coverage_type)} · {stateName(p.state)}</p>
      <p className="text-sm text-slate-500">{money(p.monthly_premium)}/mo · Effective {fmtDate(p.effective_date)}</p>
    </div>
    <div className="flex items-center gap-3">
      <StatusPill status={p.filing_status} testId={`policy-filing-status-${p.id}`} />
      <ArrowRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-1" />
    </div>
  </Link>
  <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-5 py-3">
    <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Documents</span>
    <PolicyDocButtons policy={p} size="sm" idSuffix={p.id} />
  </div>
  </div>
);

const PaymentsTable = ({ items }) => (
  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
    <table className="w-full text-sm" data-testid="payments-table">
      <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
        <tr><th className="p-3">Date</th><th className="p-3">Policy</th><th className="p-3">Type</th><th className="p-3">Method</th><th className="p-3">Amount</th></tr>
      </thead>
      <tbody>
        {items.map((t) => (
          <tr key={t.id} className="border-t border-slate-100">
            <td className="p-3">{fmtDate(t.created_at)}</td>
            <td className="p-3 font-mono">{t.policy_number}</td>
            <td className="p-3"><StatusPill status={t.type} /></td>
            <td className="p-3">{METHOD_LABEL[t.method] || t.method}</td>
            <td className="p-3 font-mono">{["refund", "credit"].includes(t.type) ? "−" : ""}{money(t.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = () =>
    Promise.all([api.get("/quotes"), api.get("/policies"), api.get("/payments"), api.get("/claims"), api.get("/support")])
      .then(([q, p, t, c, s]) => setData({ quotes: q.data, policies: p.data, payments: t.data, claims: c.data, support: s.data }))
      .catch((err) => toast.error(formatApiError(err)));
  useEffect(() => {
    load();
  }, []);
  if (!data) return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-blue-700" /></div>;
  const openQuotes = data.quotes.filter((q) => q.status !== "purchased");
  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="customer-dashboard">
      <p className="eyebrow">My account</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="dashboard-greeting">Hi, {user.name.split(" ")[0]}</h1>
      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        {[["Active policies", data.policies.filter((p) => p.status === "active").length], ["Open quotes", openQuotes.length], ["Certificates ready", data.policies.filter((p) => p.filing_status === "certificate_active").length]].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
            <p className="mt-2 font-mono text-3xl font-semibold" data-testid={`stat-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</p>
          </div>
        ))}
      </div>
      <Section title="Identity verification">
        <IdentityPanel />
      </Section>
      <Section title="My policies">
        <div className="grid gap-3">{data.policies.length ? data.policies.map((p) => <PolicyRow key={p.id} p={p} />) : <Empty testId="policies-empty" text="No policies yet. Once you pay for a priced quote, your policy appears here, ready to download or print." />}</div>
      </Section>
      <Section title="My quotes" action={<Button asChild variant="outline" size="sm" data-testid="new-quote-btn"><Link to="/quote"><Plus className="mr-1 h-4 w-4" /> New quote</Link></Button>}>
        <div className="grid gap-3">{openQuotes.length ? openQuotes.map((q) => <QuoteCard key={q.id} q={q} />) : <Empty testId="quotes-empty" text="No open quotes." />}</div>
      </Section>
      <Section title="My claims" action={<ClaimDialog policies={data.policies} onDone={load} />}>
        {data.claims.length ? <ClaimsList claims={data.claims} /> : <Empty testId="claims-empty" text={data.policies.length ? "No claims filed. If something happens, file a claim here any time." : "Claims can be filed once you have a policy."} />}
      </Section>
      <Section title="Messages" action={<NewMessageDialog policies={data.policies} onDone={load} />}>
        {data.support.length ? <SupportList requests={data.support} onDone={load} /> : <Empty testId="support-empty" text="Need something from our team, like a cancellation or coverage change? Send us a message and we'll reply here." />}
      </Section>
      <Section title="Payment history">
        {data.payments.length ? <PaymentsTable items={data.payments} /> : <Empty testId="payments-empty" text="No payments yet." />}
      </Section>
      <p className="mt-10 flex items-center gap-2 text-xs text-slate-500"><Bot className="h-4 w-4" /> Questions about your policy, payments or claims? Ask our assistant (bottom right) any time.</p>
    </div>
  );
}
